import * as React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import {
  socialDiscoveryFeedQueryFetcher,
  socialDiscoveryFeedQueryKey,
  socialDiscoveryFeedQueryRootKey,
  socialFollowersFeedQueryFetcher,
  socialFollowersFeedQueryKey,
  socialFollowersFeedQueryRootKey,
} from '@web/gen/feed'
import type { SocialDiscoveryFeedQuery } from '@web/gen/feed'

export function useSocialDiscoveryFeedInfiniteQuery({
  initialData,
}: {
  initialData?: InfiniteData<SocialDiscoveryFeedQuery>
} = {}) {
  const variables = { input: { itemsPerPage: 3 } }

  const normalizedInitialData = React.useMemo(() => {
    if (!initialData) {
      return
    }
    // oxlint-disable-next-line array-callback-return
    const pageParams = (initialData.pageParams as unknown[]).map(p => {
      // oxlint-disable-next-line eqeqeq
      if (p == null) {
        return
      }
      if (typeof p === 'number') {
        return p
      }
      const n = Number(p)
      return Number.isFinite(n) ? n : undefined
    }) as (number | null | undefined)[]
    return {
      pages: initialData.pages,
      pageParams,
    } as InfiniteData<SocialDiscoveryFeedQuery, number | null | undefined>
  }, [initialData])

  return useInfiniteQuery({
    queryKey: [socialDiscoveryFeedQueryRootKey, ...socialDiscoveryFeedQueryKey(variables)],
    queryFn: ({ pageParam }) =>
      socialDiscoveryFeedQueryFetcher({ input: { ...variables.input, cursor: pageParam } })(),
    getNextPageParam: lastPage => lastPage.socialDiscoveryFeed.pagination?.nextPageCursor,
    initialPageParam: undefined as number | null | undefined,
    initialData: normalizedInitialData,
  })
}

export function useSocialFollowersFeedInfiniteQuery() {
  const variables = { input: { itemsPerPage: 3 } }

  return useInfiniteQuery({
    queryKey: [socialFollowersFeedQueryRootKey, ...socialFollowersFeedQueryKey(variables)],
    queryFn: ({ pageParam }) =>
      socialFollowersFeedQueryFetcher({ input: { ...variables.input, cursor: pageParam } })(),
    getNextPageParam: lastPage => lastPage.socialFollowersFeed.pagination?.nextPageCursor,
    initialPageParam: undefined as number | null | undefined,
  })
}
